import { HttpErrorResponse } from "@angular/common/http";
import { ProgramStatus } from "@k12/models";

export interface ErrorItem {
  errorMessage: string;
}

export function getTokenFromUrl(url: string): string {
  const location = url.split('/');
  return location[location.length - 1];
}

export function replaceSpaceWithUnderscore(value: string): string {
  return value?.replace(/\s+/g, '_');
}

export function arrayToString(list: ErrorItem[] | { Error: string }): string {
  if (Array.isArray(list)) {
    return list.map(err => err.errorMessage).join(', ');
  } else {
    return (list as { Error: string }).Error;
  }
}

export function convertToHttps(url: string): string {
  if (!url?.trim()) return "";
  url = url.trim();
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

export function removeHyphens(id: string): string {
  return id.replace(/-/g, '');
}

// Normalizes the loosely-typed values that can come back from a FormControl (raw drafts may
// carry a string/number rather than a boolean) into a strict boolean.
export function toBool(v: boolean | string | number | null | undefined): boolean {
  if (v === true) return true;
  if (v === false || v == null) return false;
  const s = String(v).trim().toLowerCase();
  return s === 'true' || s === '1' || s === 'yes' || s === 'on';
}

/**
 * Structure (prompts, components, inputs, branches) is locked for archived programs and for
 * programs attached to an active enrollment period (Status 'Active'): they are accepting live
 * data, and the API rejects structural mutations for them. Metadata edits stay available.
 */
export function isProgramStructureLocked(status: ProgramStatus | string | null | undefined): boolean {
  return status === ProgramStatus.archived || status === ProgramStatus.active;
}

export function normalizeAccountToken(token: string | null | undefined): string {
  if (!token) return '';
  return removeHyphens(token).toUpperCase();
}

export function isSameAccountToken(tokenA: string | null | undefined, tokenB: string | null | undefined): boolean {
  if (!tokenA || !tokenB) return false;
  return normalizeAccountToken(tokenA) === normalizeAccountToken(tokenB);
}

export function errorToastValue(errorResponse: HttpErrorResponse): string {
  if (errorResponse.status === 401) return "";
  const displayText = (errorResponse.error?.length ?? 0) > 0 ? arrayToString(errorResponse.error) ?? errorResponse.error : undefined;
  // 422 guard responses (e.g. structural edits on a program accepting live data) carry the
  // server's explanation as { Error: "..." } rather than a validation-result array.
  return displayText ?? errorResponse.error?.Error ?? errorResponse.error?.message ?? errorResponse.message;
}

export function sortByDateTime<T>(list: T[], field: string): T[] {
  return [...list].sort((a,b) => {
    const date1 = new Date(a[field]).getTime();
    const date2 = new Date(b[field]).getTime();
    return date2 - date1;
  })
}

export function registerSharedIcons(matIconRegistry, domSanitizer) {
  matIconRegistry.addSvgIcon(
    'k12-edit',
    domSanitizer.bypassSecurityTrustResourceUrl('assets/images/edit.svg')
  );
  matIconRegistry.addSvgIcon(
    'k12-copy',
    domSanitizer.bypassSecurityTrustResourceUrl('assets/images/copy.svg')
  );
  matIconRegistry.addSvgIcon(
    'k12-delete',
    domSanitizer.bypassSecurityTrustResourceUrl('assets/images/trash-can.svg')
  );
  matIconRegistry.addSvgIcon(
    'k12-notes',
    domSanitizer.bypassSecurityTrustResourceUrl('assets/images/clipboard-list.svg')
  );
  matIconRegistry.addSvgIcon(
    'k12-error-circle-xmark',
    domSanitizer.bypassSecurityTrustResourceUrl('assets/images/error-circle-xmark.svg')
  );
  matIconRegistry.addSvgIcon(
    'k12-success-green',
    domSanitizer.bypassSecurityTrustResourceUrl('assets/images/success-green.svg')
  );
  matIconRegistry.addSvgIcon(
    'k12-chat-bubbles',
    domSanitizer.bypassSecurityTrustResourceUrl('assets/images/chat-bubbles.svg')
  );
  matIconRegistry.addSvgIcon(
    'k12-message-send',
    domSanitizer.bypassSecurityTrustResourceUrl('assets/images/message-send.svg')
  );
  matIconRegistry.addSvgIcon(
    'k12-messages',
    domSanitizer.bypassSecurityTrustResourceUrl('assets/images/messages.svg')
  );
  matIconRegistry.addSvgIcon(
    'k12-upload-regular',
    domSanitizer.bypassSecurityTrustResourceUrl('assets/images/upload-regular.svg')
  );
  matIconRegistry.addSvgIcon(
    'k12-warning',
    domSanitizer.bypassSecurityTrustResourceUrl('assets/images/warning-black.svg')
  );
  matIconRegistry.addSvgIcon(
    'k12-retry',
    domSanitizer.bypassSecurityTrustResourceUrl('assets/images/retry.svg')
  );
  matIconRegistry.addSvgIcon(
    'k12-circle-info',
    domSanitizer.bypassSecurityTrustResourceUrl('assets/images/circle-info.svg')
  );
  matIconRegistry.addSvgIcon(
    'k12-circle-star',
    domSanitizer.bypassSecurityTrustResourceUrl('assets/images/circle-star.svg')
  );
  matIconRegistry.addSvgIcon(
    'k12-x-mark',
    domSanitizer.bypassSecurityTrustResourceUrl('assets/images/x-mark.svg')
  );
  matIconRegistry.addSvgIcon(
    'k12-circle-xmark',
    domSanitizer.bypassSecurityTrustResourceUrl('assets/images/circle-xmark.svg')
  );
   matIconRegistry.addSvgIcon(
    'k12-circle-check',
    domSanitizer.bypassSecurityTrustResourceUrl('assets/images/circle-success.svg')
  );
  matIconRegistry.addSvgIcon(
    'k12-eye-view',
    domSanitizer.bypassSecurityTrustResourceUrl('assets/images/eye-view.svg')
  );
}
